import { consultarDocumentoReserva } from '../../../services/facturacionService';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createPago, fetchCitasByClienteIda } from '../../../services/pagosService';
import { TipoPago, fetchTiposPagoPorPos } from '../../../services/tiposPagoService';
import { obtenerSesionActivaPorId, SesionActivaResponse } from '../../../services/pos/sesionPOSService';
import { fetchPuntoDeVenta } from '../../../services/pos/puntoDeVentaService';
import { fetchCitaById } from '../../../services/citasService';
import {
    Banknote, CreditCard, X, Smartphone, Wallet, Receipt, FileText,
    CheckCircle, AlertCircle, ExternalLink, Lock, SplitSquareHorizontal,
} from 'lucide-react';

// ─── Design tokens ────────────────────────────────────────────
const C = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryDark:  '#004d7a',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.pm-wrap * { font-family:'DM Sans',sans-serif !important; box-sizing:border-box; }
@keyframes pmFadeUp  { from{opacity:0;transform:translateY(16px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes pmSpin    { to{transform:rotate(360deg);} }
@keyframes pmPulse   { 0%,100%{opacity:1;}50%{opacity:.55;} }
@keyframes pmSlideIn { from{opacity:0;transform:translateX(-8px);}to{opacity:1;transform:translateX(0);} }
.pm-doc-options { display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px; }
.pm-doc-option { position:relative;cursor:pointer;min-width:0; }
.pm-doc-option input { position:absolute;opacity:0;width:1px;height:1px; }
.pm-doc-card { display:flex;align-items:flex-start;gap:12px;height:100%;padding:16px;border:1.5px solid #e2e8f0;border-radius:16px;background:#fff;transition:background .2s,border-color .2s,box-shadow .2s,transform .2s; }
.pm-doc-icon { display:flex;align-items:center;justify-content:center;flex-shrink:0;width:40px;height:40px;border-radius:12px;background:#f1f5f9;color:#64748b; }
.pm-doc-check { margin-left:auto;color:transparent;flex-shrink:0; }
.pm-doc-option input:checked + .pm-doc-card { border-color:var(--doc-color);background:var(--doc-bg);box-shadow:0 4px 14px var(--doc-shadow); }
.pm-doc-option input:checked + .pm-doc-card .pm-doc-icon { background:var(--doc-color);color:#fff; }
.pm-doc-option input:checked + .pm-doc-card .pm-doc-check { color:var(--doc-color); }
.pm-doc-option input:focus-visible + .pm-doc-card { outline:3px solid var(--doc-color);outline-offset:3px; }
.pm-doc-option input:not(:disabled) + .pm-doc-card:hover { transform:translateY(-2px);border-color:var(--doc-color); }
.pm-doc-option input:disabled + .pm-doc-card { opacity:.55;cursor:not-allowed; }
@media(max-width:520px){.pm-doc-options{grid-template-columns:1fr}.pm-doc-card{padding:12px}}
@media(prefers-reduced-motion:reduce){.pm-doc-card{transition:none}.pm-doc-option input:not(:disabled) + .pm-doc-card:hover{transform:none}}
.pm-modal { animation:pmFadeUp .32s cubic-bezier(.16,1,.3,1) both; }
.pm-spin   { animation:pmSpin .7s linear infinite; }
.pm-pulse  { animation:pmPulse 1.8s ease-in-out infinite; }
.pm-slide  { animation:pmSlideIn .25s ease both; }
.pm-input { transition:border-color .2s, box-shadow .2s; }
.pm-input:focus { outline:none; border-color:${C.secondary} !important; box-shadow:0 0 0 3px ${C.secondary}18 !important; }
.pm-input:read-only { background:#f8fafc !important; }
.pm-input:disabled { opacity:.55; cursor:not-allowed; }
.pm-scroll::-webkit-scrollbar{width:5px;}
.pm-scroll::-webkit-scrollbar-track{background:transparent;}
.pm-scroll::-webkit-scrollbar-thumb{background:${C.secondary}30;border-radius:10px;}
.pm-scroll::-webkit-scrollbar-thumb:hover{background:${C.secondary}55;}
.pm-tipo-btn { transition:all .22s cubic-bezier(.16,1,.3,1); cursor:pointer; }
.pm-tipo-btn:hover { transform:translateY(-2px); }
.pm-btn-ghost { transition:all .18s; }
.pm-btn-ghost:hover { background:#f1f5f9 !important; }
.pm-btn-primary { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pm-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 24px ${C.secondary}35 !important; }
.pm-btn-primary:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }
`;

interface PagosModalProps {
    isOpen:        boolean;
    onClose:       () => void;
    citaId:        number;
    montoCita:     number;
    montoPagado:   number;
    /**
     * Caja EXPLÍCITA desde la que se cobra. Es la autoridad del flujo: el
     * modal la revalida por id al abrir y de nuevo justo antes de cobrar, y
     * nunca resuelve "la sesión activa" del operador por su cuenta.
     */
    sesionPosId:   number;
    /**
     * Grant del operador identificado por PIN sobre ``sesionPosId``. El backend
     * lo exige para cobrar y atribuye el asiento de caja a ese operador; sin
     * grant el cobro se bloquea en la UI (nunca se envía para fallar con 403).
     */
    operadorToken: string | null;
    /**
     * Fuera del layout POS (sin barra con selector de operador) el padre
     * gestiona el grant: este callback abre su diálogo de PIN.
     */
    onIdentifyOperator?: () => void;
    onPagoSuccess: () => void;
}

const PagosModal: React.FC<PagosModalProps> = ({
    isOpen, onClose, citaId, montoCita, montoPagado, sesionPosId, operadorToken, onIdentifyOperator, onPagoSuccess,
}) => {
    // ── Estado ────────────────────────────────────────────────
    const [tiposPago,            setTiposPago]            = useState<TipoPago[]>([]);
    const [tipoPagoId,           setTipoPagoId]           = useState<number | ''>('');
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<TipoPago | null>(null);
    const [efectivoRecibido,     setEfectivoRecibido]     = useState<number>(0);
    const [cambio,               setCambio]               = useState<number>(0);
    const [isLoading,            setIsLoading]            = useState(false);
    // Generación del grant con la que se inició el cobro en vuelo (null = ninguno).
    // "Procesando" se DERIVA de que esa generación siga siendo la vigente: un
    // cambio de operador la deja atrás en el mismo render, sin efecto tardío.
    const [processingGrant,      setProcessingGrant]      = useState<number | null>(null);
    const [error,                setError]                = useState<string | null>(null);
    const [saldoPendiente,       setSaldoPendiente]       = useState(montoCita - montoPagado);
    const [ultimoCambio,         setUltimoCambio]         = useState<number>(0);
    const [sesionActiva,         setSesionActiva]         = useState<SesionActivaResponse | null>(null);
    const [verificandoSesion,    setVerificandoSesion]    = useState(false);
    const [showNoSessionPopup,   setShowNoSessionPopup]   = useState(false);
    const documentoEditado = useRef(false);
    const [documentoReserva, setDocumentoReserva] = useState<'factura'|'prefactura'>('factura');
    useEffect(() => { let active=true; if(isOpen){documentoEditado.current=false;setDocumentoReserva('factura');consultarDocumentoReserva(Number(citaId)).then(d=>{if(active&&!documentoEditado.current)setDocumentoReserva(d.tipo);}).catch(()=>{});} return()=>{active=false;}; }, [isOpen, citaId]);
    const [paymentSuccess,       setPaymentSuccess]       = useState(false);
    const [montoAPagar,          setMontoAPagar]          = useState<number>(0);
    const [citaBranchName,       setCitaBranchName]       = useState<string | null>(null);
    const [posBranchName,        setPosBranchName]        = useState<string | null>(null);
    const [errorSucursal,        setErrorSucursal]        = useState<string | null>(null);

    const redondear = useCallback((valor: number) =>
        Math.round((valor + Number.EPSILON) * 100) / 100, []);

    // ── Identidad de la operación: cita + sesión POS + operador + generación ──
    // Toda continuación asíncrona (carga de sesión/tipos/sucursal, cobro y
    // callbacks) exige que el modal siga montado y abierto, que citaId y
    // sesionPosId sigan siendo los capturados al iniciar y que ninguna nueva
    // identidad/cierre haya incrementado la generación mientras tanto. El
    // cobro exige ADEMÁS que el grant del operador (operadorToken) siga
    // siendo exactamente el capturado al iniciar y que ningún cambio de
    // operador haya incrementado la generación del grant: nunca se cobra con
    // un token viejo aunque el operador vuelva a ser el mismo (A -> B -> A).
    const mountedRef = useRef(true);
    const generationRef = useRef(0);
    const grantGenerationRef = useRef(0);
    const identityRef = useRef({ isOpen, citaId, sesionPosId, operadorToken });
    identityRef.current = { isOpen, citaId, sesionPosId, operadorToken };
    // Cerrojo SÍNCRONO de envío: se adquiere antes del primer await y solo lo
    // libera la misma generación (identidad + grant) que lo tomó: una
    // respuesta tardía de una generación anterior jamás desbloquea la actual.
    // Un cambio de identidad/cierre/operador lo reinicia junto con la generación.
    const submittingRef = useRef(false);
    // Cambio de operador (grant) detectado EN ESTE RENDER, no en un efecto:
    // el padre entrega el token por estado (PIN resuelto fuera de un evento) y
    // React pinta el botón habilitado antes de correr los efectos pasivos de ese
    // commit. Si el operador pulsa en esa ventana, un efecto tardío incrementaría
    // la generación DESPUÉS de que el submit la capturó y abortaría un cobro con
    // el token vigente. Aquí la generación del grant ya está actualizada cuando
    // se pinta el botón, así que todo submit posterior la captura correcta. El
    // cambio invalida cualquier cobro en vuelo y libera el cerrojo para el nuevo
    // operador SIN recargar caja, tipos ni sucursal (la cita y la caja no
    // cambiaron). La respuesta tardía del grant anterior ya no es "actual": ni
    // cobra, ni notifica, ni libera.
    const lastGrantRef = useRef(operadorToken);
    if (lastGrantRef.current !== operadorToken) {
        lastGrantRef.current = operadorToken;
        grantGenerationRef.current += 1;
        submittingRef.current = false;
    }
    const isProcessingPayment = processingGrant !== null && processingGrant === grantGenerationRef.current;
    const beginOperation = useCallback((options?: { grantToken: string }) => {
        const generation = generationRef.current;
        const grantGeneration = grantGenerationRef.current;
        const cita = citaId;
        const pos = sesionPosId;
        const grant = options?.grantToken;
        return () => mountedRef.current
            && generation === generationRef.current
            && identityRef.current.isOpen
            && identityRef.current.citaId === cita
            && identityRef.current.sesionPosId === pos
            && (grant === undefined || (
                grantGeneration === grantGenerationRef.current
                && identityRef.current.operadorToken === grant
            ));
    }, [citaId, sesionPosId]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            generationRef.current += 1;
            grantGenerationRef.current += 1;
        };
    }, []);

    useEffect(() => {
        // Nueva identidad (cita/sesión) o cierre: invalida TODO lo anterior y
        // reinicia el estado. Nada de la identidad previa sobrevive.
        generationRef.current += 1;
        grantGenerationRef.current += 1;
        submittingRef.current = false;
        setProcessingGrant(null); setPaymentSuccess(false); setError(null); setErrorSucursal(null);
        setSesionActiva(null); setPosBranchName(null); setCitaBranchName(null); setShowNoSessionPopup(false);
        setTiposPago([]); setTipoPagoId(''); setTipoPagoSeleccionado(null); setUltimoCambio(0);
        if (!isOpen) { setVerificandoSesion(false); setIsLoading(false); return; }
        const isCurrent = beginOperation();

        const cargarSesionYTipos = async () => {
            setVerificandoSesion(true);
            let sesion: SesionActivaResponse | null = null;
            try {
                // Autoridad EXPLÍCITA: se revalida la sesión indicada por id.
                sesion = await obtenerSesionActivaPorId(sesionPosId);
                if (!isCurrent()) return;
                if (!sesion || sesion.id !== sesionPosId) {
                    setSesionActiva(null); setShowNoSessionPopup(true); return;
                }
                setSesionActiva(sesion);
            } catch {
                if (!isCurrent()) return;
                setSesionActiva(null); setShowNoSessionPopup(true); return;
            } finally {
                if (isCurrent()) setVerificandoSesion(false);
            }
            setIsLoading(true);
            try {
                const pos = await fetchPuntoDeVenta(sesion.punto_de_venta);
                if (!isCurrent()) return;
                setPosBranchName(pos.sucursal_info?.nombre || 'Desconocida');
                const tipos = await fetchTiposPagoPorPos(sesion.punto_de_venta);
                if (!isCurrent()) return;
                setTiposPago(tipos);
                if (tipos.length > 0) {
                    const primerTipo = tipos[0];
                    setTipoPagoId(primerTipo.id);
                    setTipoPagoSeleccionado(primerTipo);
                    const saldo = redondear(montoCita - montoPagado);
                    setMontoAPagar(saldo);
                    setEfectivoRecibido(saldo);
                }
            } catch (err) {
                if (!isCurrent()) return;
                setError('Error al cargar los tipos de pago');
                console.error(err);
            } finally {
                if (isCurrent()) setIsLoading(false);
            }
        };

        cargarSesionYTipos();
        fetchCitaById(citaId)
            .then(cita => { if (isCurrent()) setCitaBranchName(cita.sucursal_info?.nombre || 'Desconocida'); })
            .catch(() => { if (isCurrent()) setErrorSucursal('No se pudo verificar la sucursal del cliente.'); });
        fetchCitasByClienteIda(citaId)
            .then(pagos => { if (isCurrent()) setUltimoCambio(pagos.length > 0 ? Number(pagos[0].cambio) || 0 : 0); })
            .catch(() => { if (isCurrent()) setUltimoCambio(0); });
        // montoCita/montoPagado solo inicializan importes: no reinician la carga.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, citaId, sesionPosId, beginOperation]);

    useEffect(() => {
        const saldo = redondear(montoCita - montoPagado);
        setSaldoPendiente(saldo);
        if (tipoPagoSeleccionado?.configuracion_detail) {
            const config = tipoPagoSeleccionado.configuracion_detail;
            if (documentoReserva === 'factura' || !config.permite_monto_personalizado) setMontoAPagar(saldo);
            if (config.requiere_cambio) {
                setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
            } else if (config.permite_monto_personalizado && !config.afecta_caja && efectivoRecibido > montoAPagar) {
                setCambio(redondear(efectivoRecibido - montoAPagar));
            } else {
                setCambio(0);
                if (!config.afecta_caja && !config.permite_monto_personalizado) setEfectivoRecibido(redondear(montoAPagar));
            }
        } else {
            setCambio(redondear(efectivoRecibido > montoAPagar ? efectivoRecibido - montoAPagar : 0));
        }
    }, [efectivoRecibido, montoCita, montoPagado, montoAPagar, tipoPagoSeleccionado, redondear, documentoReserva]);

    const handleTipoPagoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = Number(e.target.value);
        setTipoPagoId(id);
        const tipo = tiposPago.find(t => t.id === id);
        setTipoPagoSeleccionado(tipo || null);
        const saldo = redondear(montoCita - montoPagado);
        setMontoAPagar(saldo);
        setEfectivoRecibido(saldo);
    };

    const getIconoTipoPago = (tipo: string) => {
        switch (tipo) {
            case 'efectivo': return <Banknote size={16}/>;
            case 'banco':    return <CreditCard size={16}/>;
            case 'otro':     return <Smartphone size={16}/>;
            default:         return <Wallet size={16}/>;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Doble envío (dos submits/clicks en el mismo turno, Enter + click…):
        // idempotente en la UI, una sola petición en vuelo por generación.
        if (submittingRef.current || isProcessingPayment) return;
        setError(null); setErrorSucursal(null); setPaymentSuccess(false);
        if (!sesionActiva || sesionActiva.id !== sesionPosId) { setError('No hay sesión POS activa'); return; }
        if (!operadorToken) { setError('Identifica al operador de caja con su PIN antes de cobrar.'); return; }
        const grantToken = operadorToken;
        if (citaBranchName && posBranchName && citaBranchName !== posBranchName) {
            setErrorSucursal(`No es posible registrar el pago porque la sesión POS activa pertenece a la sucursal "${posBranchName}", mientras que el cliente pertenece a la sucursal "${citaBranchName}".\n\nPor favor, cierre la sesión POS actual y abra una nueva en la sucursal "${citaBranchName}".`);
            return;
        }
        if (!tipoPagoId || !tipoPagoSeleccionado) { setError('Por favor seleccione un tipo de pago'); return; }
        const config = tipoPagoSeleccionado.configuracion_detail;
        const efectivoRedondeado = redondear(efectivoRecibido);
        const montoFinal = redondear(montoAPagar);
        if (config) {
            if (config.requiere_cambio) {
                if (efectivoRedondeado <= 0) { setError('El efectivo recibido debe ser mayor a 0'); return; }
                if (efectivoRedondeado < montoFinal) { setError('El efectivo recibido no puede ser menor al monto a pagar'); return; }
            }
            if (config.permite_monto_personalizado && montoFinal <= 0) { setError('El monto a pagar debe ser mayor a 0'); return; }
            if (config.permite_monto_personalizado && montoFinal > redondear(montoCita - montoPagado)) { setError(`El monto no puede superar el saldo pendiente de ${redondear(montoCita - montoPagado).toFixed(2)} Bs.`); return; }
        }
        if (documentoReserva === 'factura' && montoFinal !== redondear(montoCita - montoPagado)) {
            setError('Para factura debes cubrir el saldo completo de la reserva.'); return;
        }
        if (montoFinal <= 0) { setError('Debe ingresar un monto mayor a 0'); return; }
        // Identidad capturada al iniciar: cita + caja explícita + grant del
        // operador. Si cambia cualquiera (o el modal se cierra/desmonta)
        // durante una espera, la continuación se abandona: ni cobro ni
        // callbacks bajo la anterior, y nunca un createPago con token viejo.
        const targetCitaId = citaId;
        const targetPosId = sesionPosId;
        const isCurrent = beginOperation({ grantToken });
        // Cerrojo adquirido ANTES del primer await (síncrono en este turno).
        submittingRef.current = true;
        setProcessingGrant(grantGenerationRef.current);
        try {
            // Revalidar la MISMA caja por id justo antes de mutar (abortable).
            const vigente = await obtenerSesionActivaPorId(targetPosId);
            if (!isCurrent()) return;
            if (!vigente || vigente.id !== targetPosId) {
                setProcessingGrant(null); setSesionActiva(null); setShowNoSessionPopup(true); return;
            }
            const datosPago: any = { documento_reserva: documentoReserva, cita: targetCitaId, tipo_pago: tipoPagoId, monto: montoFinal, sesion_pos: targetPosId };
            if (config?.requiere_cambio) datosPago.efectivo_recibido = efectivoRedondeado;
            else if (config?.permite_monto_personalizado && !config?.afecta_caja && efectivoRedondeado > montoFinal) datosPago.efectivo_recibido = efectivoRedondeado;
            else if (config?.afecta_caja) datosPago.efectivo_recibido = montoFinal;
            // Justo antes de mutar: identidad y grant del operador vigentes
            // (el token enviado es EXACTAMENTE el que sigue identificado).
            if (!isCurrent()) return;
            await createPago(datosPago, grantToken);
            if (!isCurrent()) return;
            // Éxito SOLO tras la confirmación del servidor y solo para esta
            // identidad: se notifica de inmediato, sin espera cosmética.
            setPaymentSuccess(true); setProcessingGrant(null);
            onPagoSuccess(); onClose();
        } catch (err: any) {
            if (!isCurrent()) return;
            setProcessingGrant(null); setPaymentSuccess(false);
            if (err?.response?.status === 409 || err?.response?.status === 404) { setSesionActiva(null); setShowNoSessionPopup(true); return; }
            if (err.response?.data?.error) setError(err.response.data.error);
            else if (err.response?.data) setError(Object.values(err.response.data).flat().join(' '));
            else setError('Error al registrar el pago');
        } finally {
            // Solo la generación que lo adquirió libera el cerrojo.
            if (isCurrent()) submittingRef.current = false;
        }
    };

    const handleIrAPOS       = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleAbrirSesionPOS = () => { onClose(); window.open('/pos-list', '_blank'); };
    const handleCloseNoSessionPopup = () => { setShowNoSessionPopup(false); onClose(); };

    if (!isOpen) return null;

    // ─── Helpers de color por tipo de config ─────────────────
    const configColor = (afecta: boolean, requiere: boolean) => {
        if (!afecta)           return { bg: '#7c3aed', light: '#ede9fe', border: '#c4b5fd' };
        if (afecta && requiere) return { bg: C.secondary, light: `${C.secondary}12`, border: `${C.secondary}30` };
        return { bg: C.secondaryLight, light: `${C.secondaryLight}12`, border: `${C.secondaryLight}30` };
    };

    const config            = tipoPagoSeleccionado?.configuracion_detail;
    const permitePersonalizado = documentoReserva === 'prefactura' && (config?.permite_monto_personalizado ?? false);
    const esPagoDigitalConCambio = permitePersonalizado && !config?.afecta_caja && efectivoRecibido > montoAPagar;

    // ─── Shared input style ───────────────────────────────────
    const inputBase: React.CSSProperties = {
        width: '100%', padding: '10px 14px', borderRadius: '10px',
        border: '1.5px solid #e2e8f0', fontSize: '.88rem', color: '#0f172a',
        background: '#fff', fontFamily: "'DM Sans',sans-serif", fontWeight: '500',
    };

    // ─── OVERLAY ─────────────────────────────────────────────
    const overlay = (
        <div className="pm-wrap" style={{ position:'fixed',inset:0,zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}>
            <style>{css}</style>
            <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,.55)',backdropFilter:'blur(4px)' }} onClick={isProcessingPayment ? undefined : onClose}/>

            {/* ══ VERIFICANDO ══ */}
            {verificandoSesion && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',padding:'48px 32px',textAlign:'center',boxShadow:'0 24px 80px rgba(0,0,0,.18)',maxWidth:'380px',width:'100%' }}>
                    <div style={{ width:'52px',height:'52px',border:`3px solid ${C.secondary}20`,borderTopColor:C.secondary,borderRadius:'50%',margin:'0 auto 16px' }} className="pm-spin"/>
                    <p style={{ fontSize:'.92rem',color:'#64748b',fontWeight:'600' }}>Verificando sesión POS activa…</p>
                </div>
            )}

            {/* ══ SIN SESIÓN ══ */}
            {!verificandoSesion && showNoSessionPopup && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',maxWidth:'520px',width:'100%',boxShadow:'0 24px 80px rgba(0,0,0,.18)',overflow:'hidden' }}>
                    {/* Barra top roja */}
                    <div style={{ height:'4px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }}/>
                    {/* Header */}
                    <div style={{ padding:'24px 26px 20px',borderBottom:'1.5px solid #f1f5f9',display:'flex',alignItems:'center',gap:'14px' }}>
                        <div style={{ width:'48px',height:'48px',borderRadius:'14px',background:`${C.primary}10`,border:`1.5px solid ${C.primary}20`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                            <Lock size={22} color={C.primary}/>
                        </div>
                        <div>
                            <h3 style={{ fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.015em' }}>Sesión POS Requerida</h3>
                            <p style={{ fontSize:'.8rem',color:'#64748b',marginTop:'3px' }}>Para registrar pagos, necesita una sesión activa.</p>
                        </div>
                    </div>
                    {/* Body */}
                    <div style={{ padding:'22px 26px' }}>
                        <div style={{ background:`${C.accentDark}08`,border:`1.5px solid ${C.accentDark}25`,borderRadius:'14px',padding:'14px 16px',marginBottom:'20px',display:'flex',gap:'12px',alignItems:'flex-start' }}>
                            <AlertCircle size={16} color={C.accentDark} style={{ flexShrink:0,marginTop:'1px' }}/>
                            <div>
                                <p style={{ fontSize:'.8rem',fontWeight:'700',color:'#78350f',marginBottom:'3px' }}>Importante</p>
                                <p style={{ fontSize:'.75rem',color:'#92400e' }}>Sin una sesión POS activa, no es posible registrar pagos que afecten a la caja física.</p>
                            </div>
                        </div>
                        <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginBottom:'4px' }}>
                            {[
                                { n:1, title:'Abrir Sesión', desc:'Diríjase al módulo POS y abra una nueva sesión.' },
                                { n:2, title:'Configurar',   desc:'Establezca el monto inicial de caja y confirme.' },
                                { n:3, title:'Registrar',    desc:'Vuelva y proceda con el registro del pago.' },
                            ].map(({ n, title, desc }) => (
                                <div key={n} style={{ background:`${C.secondary}07`,border:`1.5px solid ${C.secondary}18`,borderRadius:'12px',padding:'14px 12px' }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px' }}>
                                        <div style={{ width:'22px',height:'22px',borderRadius:'50%',background:C.secondary,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                                            <span style={{ fontSize:'.6rem',fontWeight:'800',color:'#fff' }}>{n}</span>
                                        </div>
                                        <span style={{ fontSize:'.78rem',fontWeight:'700',color:'#0f172a' }}>{title}</span>
                                    </div>
                                    <p style={{ fontSize:'.7rem',color:'#64748b',lineHeight:1.45 }}>{desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Footer */}
                    <div style={{ padding:'18px 26px',borderTop:'1.5px solid #f1f5f9',background:'#fafafa',display:'flex',flexDirection:'column',gap:'10px' }}>
                        <div style={{ display:'flex',gap:'10px' }}>
                            <button className="pm-btn-ghost" onClick={handleCloseNoSessionPopup}
                                style={{ flex:1,padding:'10px',borderRadius:'11px',border:'1.5px solid #e2e8f0',background:'#fff',fontSize:'.85rem',fontWeight:'700',color:'#64748b',cursor:'pointer' }}>
                                Cancelar
                            </button>
                            <button className="pm-btn-primary" onClick={handleAbrirSesionPOS}
                                style={{ flex:1,padding:'10px',borderRadius:'11px',border:'none',background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,color:'#fff',fontSize:'.85rem',fontWeight:'800',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',boxShadow:`0 4px 16px ${C.secondary}30` }}>
                                <Banknote size={14}/> Abrir Sesión POS <ExternalLink size={12} style={{ opacity:.7 }}/>
                            </button>
                        </div>
                        <button className="pm-btn-ghost" onClick={handleIrAPOS}
                            style={{ width:'100%',padding:'9px',borderRadius:'11px',border:`1.5px solid ${C.secondary}20`,background:`${C.secondary}06`,fontSize:'.82rem',fontWeight:'700',color:C.secondary,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px' }}>
                            <CreditCard size={13}/> Ver estado actual del POS
                        </button>
                        <p style={{ fontSize:'.68rem',color:'#94a3b8',textAlign:'center' }}>Al abrir una sesión POS, acepta las políticas de control de caja.</p>
                    </div>
                </div>
            )}

            {/* ══ MODAL PRINCIPAL ══ */}
            {!verificandoSesion && !showNoSessionPopup && (
                <div className="pm-modal" style={{ position:'relative',background:'#fff',borderRadius:'22px',maxWidth:'680px',width:'100%',boxShadow:'0 24px 80px rgba(0,0,0,.18)',overflow:'hidden',maxHeight:'92vh',display:'flex',flexDirection:'column' }}>

                    {/* Barra top gradiente — azul = pago */}
                    <div style={{ height:'4px',background:`linear-gradient(90deg,${C.secondary},${C.secondaryLight},${C.accentDark},${C.primary})`,flexShrink:0 }}/>

                    {/* HEADER */}
                    <div style={{ padding:'22px 26px 18px',borderBottom:'1.5px solid #f1f5f9',flexShrink:0 }}>
                        <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px' }}>
                            <div>
                                <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'6px' }}>
                                    <div style={{ width:'36px',height:'36px',borderRadius:'11px',background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:`0 3px 12px ${C.secondary}30` }}>
                                        <CreditCard size={17} color="#fff"/>
                                    </div>
                                    <div>
                                        <h3 style={{ fontSize:'1rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em' }}>
                                            {isProcessingPayment ? 'Registrando Pago…' : 'Registrar Pago'}
                                        </h3>
                                        <p style={{ fontSize:'.75rem',color:'#94a3b8',fontWeight:'500' }}>
                                            {isProcessingPayment ? 'Por favor espere…' : 'Complete la información del pago'}
                                        </p>
                                    </div>
                                </div>
                                {sesionActiva && (
                                    <div className="pm-slide" style={{ display:'inline-flex',alignItems:'center',gap:'6px',background:`${C.secondaryLight}12`,border:`1.5px solid ${C.secondaryLight}30`,borderRadius:'100px',padding:'4px 12px' }}>
                                        <span style={{ width:'7px',height:'7px',borderRadius:'50%',background:C.secondaryLight,display:'inline-block' }}/>
                                        <span style={{ fontSize:'.7rem',fontWeight:'700',color:'#065f46' }}>Sesión POS activa</span>
                                        <span style={{ fontSize:'.68rem',color:'#94a3b8',fontWeight:'500' }}>· {sesionActiva.punto_de_venta_nombre} · {sesionActiva.codigo}</span>
                                    </div>
                                )}
                                {!operadorToken && (
                                    <p role="note" style={{ marginTop:'8px',fontSize:'.72rem',fontWeight:'700',color:'#92400e',display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap' }}>
                                        {onIdentifyOperator
                                            ? 'Identifica al operador de caja con su PIN para cobrar.'
                                            : 'Identifica al operador de caja con su PIN (barra del POS) para cobrar.'}
                                        {onIdentifyOperator && (
                                            <button type="button" onClick={onIdentifyOperator} disabled={isProcessingPayment}
                                                style={{ padding:'5px 10px',borderRadius:'8px',border:`1.5px solid ${C.secondary}`,background:'#fff',color:C.secondary,fontWeight:'800',fontSize:'.7rem',cursor:'pointer' }}>
                                                Identificar operador
                                            </button>
                                        )}
                                    </p>
                                )}
                                {operadorToken && onIdentifyOperator && (
                                    <p style={{ marginTop:'8px',fontSize:'.7rem',fontWeight:'700',color:'#166534' }}>Operador de caja identificado.</p>
                                )}
                            </div>
                            <button onClick={isProcessingPayment ? undefined : onClose} disabled={isProcessingPayment}
                                style={{ width:'32px',height:'32px',borderRadius:'9px',border:'1.5px solid #f1f5f9',background:'#f8fafc',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'background .15s' }}
                                onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                                onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                                <X size={15} color="#64748b"/>
                            </button>
                        </div>
                    </div>

                    {/* BODY */}
                    <form onSubmit={handleSubmit} style={{ display:'flex',flexDirection:'column',flex:1,minHeight:0 }}>
                        <fieldset disabled={isProcessingPayment} style={{padding:'18px 26px',margin:0,border:0,borderBottom:'1px solid #eaf0f6',background:'#fbfdff'}}>
                            <legend style={{float:'left',width:'100%',fontSize:'.85rem',fontWeight:800,color:C.secondaryDark,marginBottom:4}}>¿Qué documento necesita el cliente?</legend>
                            <div className="pm-doc-options" style={{clear:'both'}}>
                                {([{tipo:'factura',titulo:'Factura',descripcion:'Cobrar el saldo completo y emitir factura',Icono:Receipt,color:C.secondary,fondo:'#eef8ff',sombra:'#007fc814'},
                                   {tipo:'prefactura',titulo:'Prefactura',descripcion:'Recibo con opción de facturar después',Icono:FileText,color:C.primary,fondo:'#fff3f6',sombra:'#ff205214'}] as const).map(({tipo,titulo,descripcion,Icono,color,fondo,sombra})=><label key={tipo} className="pm-doc-option" style={{'--doc-color':color,'--doc-bg':fondo,'--doc-shadow':sombra} as React.CSSProperties}>
                                    <input type="radio" name="documento-reserva" aria-label={titulo} checked={documentoReserva===tipo} onChange={()=>{documentoEditado.current=true;setDocumentoReserva(tipo);}}/>
                                    <span className="pm-doc-card"><span className="pm-doc-icon"><Icono size={21}/></span><span style={{minWidth:0}}><strong style={{display:'block',fontSize:'.9rem',color:'#1e293b'}}>{titulo}</strong><span style={{display:'block',fontSize:'.73rem',lineHeight:1.5,color:'#64748b',marginTop:4}}>{descripcion}</span></span><CheckCircle size={17} className="pm-doc-check"/></span>
                                </label>)}
                            </div>
                        </fieldset>
                        <div className="pm-scroll" style={{ padding:'22px 26px',overflowY:'auto',flex:1,display:'flex',flexDirection:'column',gap:'20px' }}>

                            {/* Procesando */}
                            {isProcessingPayment && (
                                <div className="pm-pulse" style={{ background:`${C.secondary}08`,border:`1.5px solid ${C.secondary}25`,borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'center',gap:'12px' }}>
                                    <div className="pm-spin" style={{ width:'20px',height:'20px',border:`2.5px solid ${C.secondary}25`,borderTopColor:C.secondary,borderRadius:'50%',flexShrink:0 }}/>
                                    <div>
                                        <p style={{ fontSize:'.85rem',fontWeight:'700',color:C.secondaryDark }}>
                                            {paymentSuccess ? 'Pago registrado exitosamente' : 'Procesando pago…'}
                                        </p>
                                        <p style={{ fontSize:'.75rem',color:'#64748b',marginTop:'2px' }}>
                                            {paymentSuccess ? 'Actualizando información…' : 'No cierre esta ventana ni presione el botón nuevamente'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Error general */}
                            {error && !isProcessingPayment && (
                                <div style={{ background:`${C.primary}07`,borderLeft:`4px solid ${C.primary}`,borderRadius:'0 12px 12px 0',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px' }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:'10px' }}>
                                        <AlertCircle size={15} color={C.primary} style={{ flexShrink:0 }}/>
                                        <p style={{ fontSize:'.83rem',fontWeight:'600',color:C.primaryDark }}>{error}</p>
                                    </div>
                                    <button onClick={()=>setError(null)} style={{ background:'none',border:'none',cursor:'pointer',color:'#94a3b8',flexShrink:0 }}><X size={13}/></button>
                                </div>
                            )}

                            {/* Error sucursal */}
                            {errorSucursal && !isProcessingPayment && (
                                <div style={{ background:`${C.primary}07`,border:`1.5px solid ${C.primary}20`,borderRadius:'14px',padding:'16px' }}>
                                    <div style={{ display:'flex',alignItems:'flex-start',gap:'12px' }}>
                                        <div style={{ width:'32px',height:'32px',borderRadius:'9px',background:`${C.primary}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}><AlertCircle size={15} color={C.primary}/></div>
                                        <div style={{ flex:1 }}>
                                            <p style={{ fontSize:'.83rem',fontWeight:'800',color:C.primaryDark,marginBottom:'6px' }}>Sucursal incorrecta</p>
                                            <p style={{ fontSize:'.8rem',color:C.primaryDark,lineHeight:1.6,whiteSpace:'pre-line' }}>{errorSucursal}</p>
                                            <button type="button" onClick={()=>{ onClose(); window.open('/pos-list','_blank'); }}
                                                style={{ marginTop:'10px',display:'inline-flex',alignItems:'center',gap:'5px',background:`${C.primary}12`,border:`1px solid ${C.primary}25`,borderRadius:'8px',padding:'5px 12px',fontSize:'.72rem',fontWeight:'700',color:C.primaryDark,cursor:'pointer' }}>
                                                <ExternalLink size={11}/> Ir a cambiar sesión POS
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── RESUMEN DE MONTOS ── */}
                            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px' }}>
                                {[
                                    { label:'Total Cita',    val:montoCita,              icon:<Banknote size={15}/>,  color:C.secondary,       bg:`${C.secondary}10` },
                                    { label:'Pagado',        val:montoPagado,            icon:<CheckCircle size={15}/>,color:C.secondaryLight, bg:`${C.secondaryLight}12` },
                                    { label:'Último Cambio', val:ultimoCambio,           icon:<Wallet size={15}/>,    color:'#64748b',         bg:'#f1f5f9' },
                                    { label:'Pendiente',     val:Math.max(0,saldoPendiente), icon:<Banknote size={15}/>, color:saldoPendiente>0?C.accentDark:'#64748b', bg:saldoPendiente>0?`${C.accentDark}12`:'#f1f5f9' },
                                ].map(item => (
                                    <div key={item.label} style={{ background:'#fff',border:'1.5px solid #f1f5f9',borderRadius:'16px',padding:'16px 14px',boxShadow:'0 1px 8px rgba(0,0,0,.04)' }}>
                                        <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px' }}>
                                            <div style={{ width:'28px',height:'28px',borderRadius:'8px',background:item.bg,display:'flex',alignItems:'center',justifyContent:'center',color:item.color,flexShrink:0 }}>{item.icon}</div>
                                            <span style={{ fontSize:'.65rem',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.07em' }}>{item.label}</span>
                                        </div>
                                        <div style={{ fontSize:'1.05rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em' }}>
                                            {item.val.toFixed(2)} <span style={{ fontSize:'.72rem',fontWeight:'500',color:'#94a3b8' }}>Bs.</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── TIPO DE PAGO ── */}
                            <div>
                                <label style={{ display:'block',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                    Tipo de Pago <span style={{ color:C.primary }}>*</span>
                                </label>
                                <select value={tipoPagoId} onChange={handleTipoPagoChange}
                                    disabled={isLoading || !sesionActiva || isProcessingPayment || !!errorSucursal}
                                    className="pm-input"
                                    style={{ ...inputBase, appearance:'none', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 14px center', paddingRight:'36px' }}>
                                    {tiposPago.map(tipo => <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>)}
                                </select>

                                {/* Badge config */}
                                {config && (() => {
                                    const cc = configColor(config.afecta_caja, config.requiere_cambio);
                                    return (
                                        <div style={{ marginTop:'12px',background:cc.light,border:`1.5px solid ${cc.border}`,borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'flex-start',gap:'12px' }}>
                                            <div style={{ width:'34px',height:'34px',borderRadius:'10px',background:cc.light,border:`1.5px solid ${cc.border}`,display:'flex',alignItems:'center',justifyContent:'center',color:cc.bg,flexShrink:0 }}>
                                                {getIconoTipoPago(config.tipo)}
                                            </div>
                                            <div style={{ flex:1 }}>
                                                <p style={{ fontSize:'.85rem',fontWeight:'800',color:'#0f172a',marginBottom:'8px' }}>{config.nombre}</p>
                                                <div style={{ display:'flex',flexWrap:'wrap',gap:'6px' }}>
                                                    <span style={{ display:'inline-flex',alignItems:'center',gap:'3px',background:config.afecta_caja?`${C.secondaryLight}20`:'#ede9fe',border:`1px solid ${config.afecta_caja?C.secondaryLight:'#c4b5fd'}`,borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:config.afecta_caja?'#065f46':'#6d28d9' }}>
                                                        {config.afecta_caja ? 'Afecta a caja' : 'No afecta a caja'}
                                                    </span>
                                                    {config.requiere_cambio && <span style={{ display:'inline-flex',alignItems:'center',background:`${C.secondary}15`,border:`1px solid ${C.secondary}30`,borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:C.secondaryDark }}>Requiere cambio</span>}
                                                    {config.permite_monto_personalizado && <span style={{ display:'inline-flex',alignItems:'center',gap:'3px',background:'#ede9fe',border:'1px solid #c4b5fd',borderRadius:'100px',padding:'3px 9px',fontSize:'.65rem',fontWeight:'700',color:'#6d28d9' }}><SplitSquareHorizontal size={10}/> Pago parcial</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* ── MONTO A PAGAR ── */}
                            <div>
                                <label style={{ display:'flex',alignItems:'center',gap:'7px',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                    Monto a Pagar <span style={{ color:C.primary }}>*</span>
                                    {permitePersonalizado && <span style={{ fontSize:'.65rem',fontWeight:'700',background:'#ede9fe',color:'#6d28d9',borderRadius:'100px',padding:'2px 8px' }}>Monto libre</span>}
                                </label>
                                <input type="number" min="0.01" step="0.01" max={redondear(montoCita - montoPagado)}
                                    value={montoAPagar}
                                    onChange={e => {
                                        if (permitePersonalizado) {
                                            const val = parseFloat(e.target.value);
                                            const nm = isNaN(val) ? 0 : redondear(Math.min(val, montoCita - montoPagado));
                                            setMontoAPagar(nm);
                                            if (!config?.requiere_cambio) setEfectivoRecibido(nm);
                                        }
                                    }}
                                    readOnly={!permitePersonalizado || isProcessingPayment}
                                    disabled={isProcessingPayment || !!errorSucursal}
                                    className="pm-input"
                                    style={{ ...inputBase, borderColor: permitePersonalizado ? '#c4b5fd' : '#e2e8f0', background: permitePersonalizado ? '#fff' : '#f8fafc', fontWeight:'700', fontSize:'1rem' }}
                                />
                                {permitePersonalizado && <p style={{ fontSize:'.72rem',color:'#7c3aed',fontWeight:'500',marginTop:'5px' }}>Puede ingresar un monto parcial (máx: {redondear(montoCita - montoPagado).toFixed(2)} Bs.)</p>}
                            </div>

                            {/* ── EFECTIVO RECIBIDO ── */}
                            {(config?.afecta_caja || esPagoDigitalConCambio || (permitePersonalizado && !config?.afecta_caja)) && (
                                <div>
                                    <label style={{ display:'flex',alignItems:'center',gap:'7px',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                        {config?.requiere_cambio || esPagoDigitalConCambio ? 'Monto Recibido *' : 'Monto a Pagar'}
                                        {!config?.afecta_caja && permitePersonalizado && <span style={{ fontSize:'.65rem',fontWeight:'700',background:'#ede9fe',color:'#6d28d9',borderRadius:'100px',padding:'2px 8px' }}>QR / Digital</span>}
                                    </label>
                                    <input type="number" min="0" step="0.01" value={efectivoRecibido}
                                        onChange={e => { const v=parseFloat(e.target.value); setEfectivoRecibido(isNaN(v)?0:redondear(v)); }}
                                        className="pm-input"
                                        style={{ ...inputBase, fontWeight:'700', fontSize:'1rem' }}
                                        disabled={isLoading || isProcessingPayment || (!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja)) || !!errorSucursal}
                                        readOnly={(!config?.requiere_cambio && !(permitePersonalizado && !config?.afecta_caja)) || isProcessingPayment}
                                    />
                                    <p style={{ fontSize:'.72rem',color:'#94a3b8',fontWeight:'500',marginTop:'5px' }}>
                                        {config?.requiere_cambio ? 'Ingrese el monto recibido para calcular el cambio'
                                            : permitePersonalizado && !config?.afecta_caja ? 'Si recibe más del monto, el cambio se registrará como salida de caja en efectivo'
                                            : 'Pago exacto — no requiere cambio'}
                                    </p>
                                </div>
                            )}

                            {/* ── CAMBIO ── */}
                            {(config?.requiere_cambio || esPagoDigitalConCambio) && (
                                <div>
                                    <label style={{ display:'flex',alignItems:'center',gap:'7px',fontSize:'.8rem',fontWeight:'700',color:'#374151',marginBottom:'8px' }}>
                                        Cambio a Entregar
                                        {esPagoDigitalConCambio && !config?.afecta_caja && <span style={{ fontSize:'.65rem',fontWeight:'700',background:`${C.accentDark}12`,color:C.accentDark,borderRadius:'100px',padding:'2px 8px',border:`1px solid ${C.accentDark}25` }}>Salida de caja</span>}
                                    </label>
                                    <div style={{ ...inputBase, background: cambio>0?`${C.secondaryLight}10`:'#f8fafc', border:`1.5px solid ${cambio>0?C.secondaryLight:'#e2e8f0'}`, color: cambio>0?'#065f46':'#0f172a', fontWeight:'800', fontSize:'1.05rem', borderRadius:'10px' }}>
                                        {cambio.toFixed(2)} <span style={{ fontSize:'.75rem',fontWeight:'500',color:'#94a3b8' }}>Bs.</span>
                                    </div>
                                    {esPagoDigitalConCambio && cambio > 0 && <p style={{ fontSize:'.72rem',color:C.accentDark,fontWeight:'600',marginTop:'5px' }}>Este cambio se registrará como salida de caja física de {cambio.toFixed(2)} Bs.</p>}
                                </div>
                            )}

                            {/* ── ADVERTENCIA DIGITAL ── */}
                            {!config?.afecta_caja && tipoPagoSeleccionado && !isProcessingPayment && !esPagoDigitalConCambio && (
                                <div style={{ background:'#ede9fe',border:'1.5px solid #c4b5fd',borderRadius:'14px',padding:'14px 16px',display:'flex',alignItems:'flex-start',gap:'12px' }}>
                                    <div style={{ width:'34px',height:'34px',borderRadius:'10px',background:'#ddd6fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}><Smartphone size={16} color="#6d28d9"/></div>
                                    <div>
                                        <p style={{ fontSize:'.83rem',fontWeight:'800',color:'#4c1d95',marginBottom:'3px' }}>Pago Digital</p>
                                        <p style={{ fontSize:'.78rem',color:'#6d28d9',lineHeight:1.55 }}>
                                            {permitePersonalizado ? 'Si el cliente envía más del monto indicado, ingrese el monto recibido para calcular el cambio a devolver.' : 'Este pago no afectará a la caja física. El movimiento de caja no se registrará.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* FOOTER */}
                        <div style={{ padding:'18px 26px',borderTop:'1.5px solid #f1f5f9',background:'#fafafa',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px' }}>
                            {isProcessingPayment
                                ? <p style={{ fontSize:'.72rem',color:'#94a3b8',fontWeight:'500' }}>Por favor espere, esto puede tomar unos segundos…</p>
                                : <div/>
                            }
                            <div style={{ display:'flex',gap:'10px' }}>
                                <button type="button" onClick={onClose} disabled={isProcessingPayment} className="pm-btn-ghost"
                                    style={{ padding:'10px 20px',borderRadius:'11px',border:'1.5px solid #e2e8f0',background:'#fff',fontSize:'.85rem',fontWeight:'700',color:'#64748b',cursor:'pointer' }}>
                                    Cancelar
                                </button>
                                <button type="submit" disabled={isLoading||saldoPendiente<=0||!sesionActiva||!operadorToken||isProcessingPayment||!!errorSucursal} className="pm-btn-primary"
                                    title={!operadorToken?'Identifica al operador de caja con su PIN para cobrar':undefined}
                                    style={{ padding:'10px 24px',borderRadius:'11px',border:'none',background:(!isLoading&&saldoPendiente>0&&sesionActiva&&operadorToken&&!isProcessingPayment&&!errorSucursal)?`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`:'#e2e8f0',color:(!isLoading&&saldoPendiente>0&&sesionActiva&&operadorToken&&!isProcessingPayment&&!errorSucursal)?'#fff':'#94a3b8',fontSize:'.88rem',fontWeight:'800',cursor:(!isLoading&&saldoPendiente>0&&sesionActiva&&operadorToken&&!isProcessingPayment&&!errorSucursal)?'pointer':'not-allowed',display:'flex',alignItems:'center',gap:'8px',minWidth:'160px',justifyContent:'center',boxShadow:(!isLoading&&saldoPendiente>0&&sesionActiva&&operadorToken&&!isProcessingPayment&&!errorSucursal)?`0 4px 16px ${C.secondary}30`:'none' }}>
                                    {isProcessingPayment ? (
                                        <><div className="pm-spin" style={{ width:'14px',height:'14px',border:'2px solid rgba(255,255,255,.35)',borderTopColor:'#fff',borderRadius:'50%' }}/> Registrando…</>
                                    ) : (
                                        <><CreditCard size={15}/> Registrar Pago</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );

    return ReactDOM.createPortal(overlay, document.body);
};

export default PagosModal;


